import WPTypography from '@/components/typography/typography';
import { useMedia, Button } from '@drivekyte/ui';
import CategoriesSelection from '@/pages/blog/components/categories-selection';
import {
  Categories,
  Category,
} from '@/pages/blog/components/categories-selection/types';
import MainPost from '@/pages/blog/components/main-post/index';
import PostsList from '@/pages/blog/components/posts-list';
import { MainBlogContainer } from '@/pages/blog/components/styled-components';
import { Post } from '@/pages/blog/types';
import { Box, useTheme } from '@drivekyte/web-components';
import Head from 'next/head';
import { useEffect, useState } from 'react';
import { MainBlogPageProps } from './types';
import useAnalyticsWithSource from '@/hooks/use-analytics-with-source';
import { useRouter } from 'next/router';
import NavBar from '@/pages/home-page/components/nav-bar/nav-bar';
import { SearchbarToggleProvider } from '@/pages/home-page/components/nav-bar/desktop-nav-bar-search/search-bar-context';
import { getNavBarData } from '@/utils/strapi-cms/cms-utils';

const MainBlogPage = ({ data }: MainBlogPageProps) => {
  const { query, pathname } = useRouter();
  const { trackPageView } = useAnalyticsWithSource();
  const blogPosts = data?.blogPosts || [];
  const mainPageConfig = data?.mainPageConfig || null;
  const navBarData = getNavBarData(data?.mainPageConfig.navigationBar);

  const numberOfPostsToShowInitially =
    mainPageConfig?.numberOfPostsToShowInitially || 9;
  const numberOfPostsToLoad = mainPageConfig?.numberOfPostsToLoad || 6;
  const loadMoreButtonLabel = mainPageConfig?.loadMoreButtonLabel || 'More';
  const leadBlogPost = mainPageConfig?.leadBlogPost?.data?.attributes;
  const leadBlogPostSlug = mainPageConfig?.leadBlogPost?.data?.attributes?.slug;

  const media = useMedia();
  const isMobile = media.xs;
  const [theme] = useTheme();
  const [displayedPostsNumber, setDisplayedPostsNumber] = useState<number>(
    numberOfPostsToShowInitially,
  );
  const [selectedCategory, setSelectedCategory] = useState<Category>(
    Categories.All,
  );

  useEffect(() => {
    trackPageView(pathname, {
      query: query,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const handleLoadMore = () => {
    setDisplayedPostsNumber((prevValue) => prevValue + numberOfPostsToLoad);
  };

  /* Filtered blog posts */
  let filteredBlogPosts = blogPosts;
  // show lead blog post with other posts on mobile but not on web
  if (isMobile) {
    const leadBlogPostData = mainPageConfig?.leadBlogPost?.data;
    const otherBlogPosts = filteredBlogPosts.filter(
      (post) => post.attributes.slug !== leadBlogPostSlug,
    );
    filteredBlogPosts = [leadBlogPostData, ...otherBlogPosts];
  } else {
    filteredBlogPosts = filteredBlogPosts.filter(
      (post) => post.attributes.slug !== leadBlogPostSlug,
    );
  }
  // filter by selected category
  filteredBlogPosts = filteredBlogPosts.filter(
    (post: Post) =>
      selectedCategory === Categories.All ||
      post?.attributes?.category === selectedCategory,
  );
  // only show specified number of posts
  const displayedBlogPosts = filteredBlogPosts.slice(0, displayedPostsNumber);

  return (
    <>
      <Head>
        <title>Kyte Blog</title>
      </Head>
      <SearchbarToggleProvider
        isStickySearchBar={navBarData.fixedSearchOnScroll}
      >
        <NavBar navState={navBarData.variant} color={navBarData.color} />
      </SearchbarToggleProvider>
      <MainBlogContainer
        mobilePaddingTop={theme.spacings.small * 6}
        mobilePaddingBottom={theme.spacings.small}
        webPaddingTop={80}
        webPaddingBottom={80}
      >
        {isMobile ? (
          <Box paddingHorizontal="medium" paddingVertical="small">
            <WPTypography
              variant="h2"
              color="primary"
              fontFamily="Gilroy"
              fontSize={28}
              lineHeight={36}
              fontWeight={700}
            >
              Blog
            </WPTypography>
          </Box>
        ) : (
          <MainPost
            title={leadBlogPost?.title}
            author={leadBlogPost?.author?.name}
            ctaButtonLabel={mainPageConfig?.leadBlogPostButtonLabel}
            slug={leadBlogPostSlug}
            date={leadBlogPost?.date}
            image={leadBlogPost?.headlineImage}
          />
        )}
        <CategoriesSelection
          category={selectedCategory}
          onCategoryChange={(nextValue) =>
            setSelectedCategory(nextValue as Category)
          }
        />
        <PostsList mainPageConfig={mainPageConfig} posts={displayedBlogPosts} />

        {filteredBlogPosts.length === 0 && (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            marginTop="small"
          >
            <WPTypography
              variant="p"
              color="primary"
              fontSize={18}
              lineHeight={18}
              fontWeight={100}
            >
              No blog posts in this category yet
            </WPTypography>
          </Box>
        )}

        {displayedPostsNumber < filteredBlogPosts.length &&
          selectedCategory === Categories.All && (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              marginTop="small"
            >
              <Button variant="primary" onPress={handleLoadMore}>
                {loadMoreButtonLabel}
              </Button>
            </Box>
          )}
      </MainBlogContainer>
    </>
  );
};

export default MainBlogPage;
